import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import RequireTerms from "@/components/RequireTerms";
import CookieConsent from "@/components/CookieConsent";
import SeoHead from "@/components/SeoHead";
import AdminBetaPreviewIndicator from "@/components/AdminBetaPreviewIndicator";

// Eager: landing + auth + most-used flows (kept in main bundle for instant load)
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import NewInvoice from "./pages/NewInvoice.tsx";
import NotFound from "./pages/NotFound.tsx";

// Lazy: heavy or rarely-visited pages → split into separate chunks
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail.tsx"));
const EditInvoice = lazy(() => import("./pages/EditInvoice.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const SelectPlan = lazy(() => import("./pages/SelectPlan.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const AcceptTerms = lazy(() => import("./pages/AcceptTerms.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <p className="text-muted-foreground">Se încarcă...</p>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <AuthProvider>
        <TooltipProvider delayDuration={300}>
          <SeoHead />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<RequireTerms><Dashboard /></RequireTerms>} />
                <Route path="/select-plan" element={<RequireTerms><SelectPlan /></RequireTerms>} />
                <Route path="/invoice/new" element={<RequireTerms><NewInvoice /></RequireTerms>} />
                <Route path="/invoice/:id" element={<RequireTerms><InvoiceDetail /></RequireTerms>} />
                <Route path="/invoice/:id/edit" element={<RequireTerms><EditInvoice /></RequireTerms>} />
                <Route path="/settings" element={<RequireTerms><Settings /></RequireTerms>} />
                <Route path="/admin" element={<RequireTerms><Admin /></RequireTerms>} />

                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-terms" element={<AcceptTerms />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
            <AdminBetaPreviewIndicator />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
